import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useTheme } from '../ThemeContext';
import {
  GPUComposer,
  GPUProgram,
  GPULayer,
  INT,
  FLOAT,
  REPEAT,
  NEAREST,
  LINEAR,
} from 'gpu-io';

// ─── WebGL helpers (cyber theme) ─────────────────────────────────────────────

function createShader(gl, type, source) {
  const shader = gl.createShader(type);
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    console.error('[ShaderBackground] compile error:', gl.getShaderInfoLog(shader));
    gl.deleteShader(shader);
    return null;
  }
  return shader;
}

function createProgram(gl, vertSrc, fragSrc) {
  const vert = createShader(gl, gl.VERTEX_SHADER, vertSrc);
  const frag = createShader(gl, gl.FRAGMENT_SHADER, fragSrc);
  if (!vert || !frag) return null;
  const program = gl.createProgram();
  gl.attachShader(program, vert);
  gl.attachShader(program, frag);
  gl.linkProgram(program);
  gl.deleteShader(vert);
  gl.deleteShader(frag);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    console.error('[ShaderBackground] link error:', gl.getProgramInfoLog(program));
    gl.deleteProgram(program);
    return null;
  }
  return program;
}

// ─── GLSL (cyber theme) ───────────────────────────────────────────────────────

const VERT_SRC = `
attribute vec2 a_position;
void main() { gl_Position = vec4(a_position, 0.0, 1.0); }
`;

const FRAG_CYBER = `
precision mediump float;
uniform float u_time;
uniform vec2 u_resolution;
void main() {
  vec2 uv = gl_FragCoord.xy / u_resolution.xy;
  vec2 p = uv * 2.0 - 1.0;
  p.x *= u_resolution.x / u_resolution.y;
  float t = u_time * 0.5;

  float v = 0.0;
  v += sin(p.x * 3.0 + t);
  v += sin(p.y * 2.0 + t * 0.8);
  v += sin((p.x + p.y) * 2.5 + t * 1.1);
  v += sin(sqrt(p.x * p.x + p.y * p.y + 0.0001) * 4.0 - t * 1.3);
  float f = v * 0.25 * 0.5 + 0.5;

  vec3 dark    = vec3(0.039, 0.039, 0.059);
  vec3 magenta = vec3(1.0, 0.0, 1.0);
  vec3 cyan    = vec3(0.0, 0.831, 1.0);
  vec3 purple  = vec3(0.749, 0.0, 1.0);

  vec3 neon = mix(magenta, cyan, sin(f * 6.28318 + t * 0.4) * 0.5 + 0.5);
  neon      = mix(neon, purple, sin(f * 3.14159 - t * 0.6) * 0.5 + 0.5);

  float intensity = smoothstep(0.2, 0.8, f) * 0.65 + 0.05;
  gl_FragColor = vec4(mix(dark, neon, intensity), 1.0);
}
`;

// ─── Velocity fluid canvas (clean theme) ─────────────────────────────────────
//
// Solves Navier-Stokes on the GPU and renders the velocity field as a vector
// field (arrows). Mouse drag injects velocity into the simulation.

const VELOCITY_SCALE_FACTOR = 8;
const NUM_JACOBI_STEPS = 3;
const PRESSURE_CALC_ALPHA = -1;
const PRESSURE_CALC_BETA = 0.25;
const TOUCH_FORCE_SCALE = 2;
const MAX_VELOCITY = 30;

function FluidCanvas() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    let composer;
    let velocityState, divergenceState, pressureState;
    let advection, divergence2D, jacobi, gradientSubtraction, touchProgram;
    let animFrameId;

    try {
      composer = new GPUComposer({ canvas });
    } catch (e) {
      console.warn('[FluidCanvas] gpu-io init failed:', e);
      return;
    }

    const w0 = window.innerWidth;
    const h0 = window.innerHeight;
    const velW = Math.ceil(w0 / VELOCITY_SCALE_FACTOR);
    const velH = Math.ceil(h0 / VELOCITY_SCALE_FACTOR);

    velocityState = new GPULayer(composer, {
      name: 'velocity',
      dimensions: [velW, velH],
      type: FLOAT,
      filter: LINEAR,
      numComponents: 2,
      wrapX: REPEAT,
      wrapY: REPEAT,
      numBuffers: 2,
    });

    divergenceState = new GPULayer(composer, {
      name: 'divergence',
      dimensions: [velW, velH],
      type: FLOAT,
      filter: NEAREST,
      numComponents: 1,
      wrapX: REPEAT,
      wrapY: REPEAT,
    });

    pressureState = new GPULayer(composer, {
      name: 'pressure',
      dimensions: [velW, velH],
      type: FLOAT,
      filter: NEAREST,
      numComponents: 1,
      wrapX: REPEAT,
      wrapY: REPEAT,
      numBuffers: 2,
    });

    advection = new GPUProgram(composer, {
      name: 'advection',
      fragmentShader: `
      in vec2 v_uv;
      uniform sampler2D u_state;
      uniform sampler2D u_velocity;
      uniform vec2 u_dimensions;
      out vec2 out_state;
      void main() {
        out_state = texture(u_state, v_uv - texture(u_velocity, v_uv).xy / u_dimensions).xy;
      }`,
      uniforms: [
        { name: 'u_state',      value: 0,        type: INT },
        { name: 'u_velocity',   value: 1,        type: INT },
        { name: 'u_dimensions', value: [w0, h0], type: FLOAT },
      ],
    });

    divergence2D = new GPUProgram(composer, {
      name: 'divergence2D',
      fragmentShader: `
      in vec2 v_uv;
      uniform sampler2D u_vectorField;
      uniform vec2 u_pxSize;
      out float out_divergence;
      void main() {
        float n = texture(u_vectorField, v_uv + vec2(0, u_pxSize.y)).y;
        float s = texture(u_vectorField, v_uv - vec2(0, u_pxSize.y)).y;
        float e = texture(u_vectorField, v_uv + vec2(u_pxSize.x, 0)).x;
        float w = texture(u_vectorField, v_uv - vec2(u_pxSize.x, 0)).x;
        out_divergence = 0.5 * (e - w + n - s);
      }`,
      uniforms: [
        { name: 'u_vectorField', value: 0, type: INT },
        { name: 'u_pxSize', value: [1 / velW, 1 / velH], type: FLOAT },
      ],
    });

    jacobi = new GPUProgram(composer, {
      name: 'jacobi',
      fragmentShader: `
      in vec2 v_uv;
      uniform float u_alpha;
      uniform float u_beta;
      uniform vec2 u_pxSize;
      uniform sampler2D u_previousState;
      uniform sampler2D u_divergence;
      out vec4 out_jacobi;
      void main() {
        vec4 n = texture(u_previousState, v_uv + vec2(0, u_pxSize.y));
        vec4 s = texture(u_previousState, v_uv - vec2(0, u_pxSize.y));
        vec4 e = texture(u_previousState, v_uv + vec2(u_pxSize.x, 0));
        vec4 w = texture(u_previousState, v_uv - vec2(u_pxSize.x, 0));
        vec4 d = texture(u_divergence, v_uv);
        out_jacobi = (n + s + e + w + u_alpha * d) * u_beta;
      }`,
      uniforms: [
        { name: 'u_alpha',         value: PRESSURE_CALC_ALPHA, type: FLOAT },
        { name: 'u_beta',          value: PRESSURE_CALC_BETA,  type: FLOAT },
        { name: 'u_pxSize', value: [1 / velW, 1 / velH], type: FLOAT },
        { name: 'u_previousState', value: 0, type: INT },
        { name: 'u_divergence',    value: 1, type: INT },
      ],
    });

    gradientSubtraction = new GPUProgram(composer, {
      name: 'gradientSubtraction',
      fragmentShader: `
      in vec2 v_uv;
      uniform vec2 u_pxSize;
      uniform sampler2D u_scalarField;
      uniform sampler2D u_vectorField;
      out vec2 out_result;
      void main() {
        float n = texture(u_scalarField, v_uv + vec2(0, u_pxSize.y)).r;
        float s = texture(u_scalarField, v_uv - vec2(0, u_pxSize.y)).r;
        float e = texture(u_scalarField, v_uv + vec2(u_pxSize.x, 0)).r;
        float w = texture(u_scalarField, v_uv - vec2(u_pxSize.x, 0)).r;
        out_result = texture(u_vectorField, v_uv).xy - 0.5 * vec2(e - w, n - s);
      }`,
      uniforms: [
        { name: 'u_pxSize',      value: [1 / velW, 1 / velH], type: FLOAT },
        { name: 'u_scalarField', value: 0, type: INT },
        { name: 'u_vectorField', value: 1, type: INT },
      ],
    });

    touchProgram = new GPUProgram(composer, {
      name: 'touch',
      fragmentShader: `
      in vec2 v_uv;
      in vec2 v_uv_local;
      uniform sampler2D u_velocity;
      uniform vec2 u_vector;
      out vec2 out_velocity;
      void main() {
        vec2 radialVec = (v_uv_local * 2.0 - 1.0);
        float radiusSq = dot(radialVec, radialVec);
        vec2 velocity = texture(u_velocity, v_uv).xy
                      + (1.0 - radiusSq) * u_vector * ${TOUCH_FORCE_SCALE.toFixed(1)};
        float mag = length(velocity);
        out_velocity = velocity / mag * min(mag, ${MAX_VELOCITY.toFixed(1)});
      }`,
      uniforms: [
        { name: 'u_velocity', value: 0,      type: INT },
        { name: 'u_vector',   value: [0, 0], type: FLOAT },
      ],
    });

    // ── Simulation + velocity-field render loop ───────────────────────────────
    function loop() {
      composer.step({ program: advection,           input: [velocityState, velocityState], output: velocityState });
      composer.step({ program: divergence2D,        input: velocityState,                  output: divergenceState });
      for (let i = 0; i < NUM_JACOBI_STEPS; i++) {
        composer.step({ program: jacobi,            input: [pressureState, divergenceState], output: pressureState });
      }
      composer.step({ program: gradientSubtraction, input: [pressureState, velocityState], output: velocityState });

      // Render velocity field as vector arrows on a white background
      composer.drawLayerAsVectorField({
        layer:         velocityState,
        vectorSpacing: 10,
        vectorScale:   2.5,
        color:         [0.31, 0.27, 0.90], // indigo to match clean theme
      });
    }

    function animate() {
      loop();
      animFrameId = requestAnimationFrame(animate);
    }
    animFrameId = requestAnimationFrame(animate);

    // ── Pointer interaction ───────────────────────────────────────────────────
    const activeTouches = {};

    function onPointerMove(e) {
      const rect = canvas.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;
      if (e.clientX < rect.left || e.clientX > rect.right ||
          e.clientY < rect.top  || e.clientY > rect.bottom) {
        delete activeTouches[e.pointerId];
        return;
      }

      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      if (activeTouches[e.pointerId] === undefined) {
        activeTouches[e.pointerId] = { current: [x, y] };
        return;
      }
      activeTouches[e.pointerId].last    = activeTouches[e.pointerId].current;
      activeTouches[e.pointerId].current = [x, y];
      const { current, last } = activeTouches[e.pointerId];
      if (current[0] === last[0] && current[1] === last[1]) return;

      touchProgram.setUniform('u_vector', [current[0] - last[0], -(current[1] - last[1])]);
      composer.stepSegment({
        program:   touchProgram,
        input:     velocityState,
        output:    velocityState,
        position1: [current[0], rect.height - current[1]],
        position2: [last[0],    rect.height - last[1]],
        thickness: 30,
        endCaps:   true,
      });
    }

    function onPointerStop(e) { delete activeTouches[e.pointerId]; }

    document.addEventListener('pointermove',   onPointerMove);
    document.addEventListener('pointerup',     onPointerStop);
    document.addEventListener('pointerout',    onPointerStop);
    document.addEventListener('pointercancel', onPointerStop);

    // ── Resize ────────────────────────────────────────────────────────────────
    function onResize() {
      const width  = window.innerWidth;
      const height = window.innerHeight;
      const vw = Math.ceil(width  / VELOCITY_SCALE_FACTOR);
      const vh = Math.ceil(height / VELOCITY_SCALE_FACTOR);

      composer.resize([width, height]);
      velocityState.resize([vw, vh]);
      divergenceState.resize([vw, vh]);
      pressureState.resize([vw, vh]);

      advection.setUniform('u_dimensions', [width, height]);
      const px = [1 / vw, 1 / vh];
      divergence2D.setUniform('u_pxSize', px);
      jacobi.setUniform('u_pxSize', px);
      gradientSubtraction.setUniform('u_pxSize', px);
    }

    onResize();
    window.addEventListener('resize', onResize);

    return () => {
      cancelAnimationFrame(animFrameId);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('pointermove',   onPointerMove);
      document.removeEventListener('pointerup',     onPointerStop);
      document.removeEventListener('pointerout',    onPointerStop);
      document.removeEventListener('pointercancel', onPointerStop);
      velocityState?.dispose();
      divergenceState?.dispose();
      pressureState?.dispose();
      advection?.dispose();
      divergence2D?.dispose();
      jacobi?.dispose();
      gradientSubtraction?.dispose();
      touchProgram?.dispose();
      composer?.dispose();
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        top: 0, left: 0, right: 0, bottom: 0,
        width: '100%',
        height: '100%',
        display: 'block',
        pointerEvents: 'none',
      }}
    />
  );
}

// ─── Cyber canvas ─────────────────────────────────────────────────────────────

function ShaderCanvas() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const gl = canvas.getContext('webgl', { alpha: false, antialias: false });
    if (!gl) { console.warn('[ShaderBackground] WebGL not supported'); return; }

    const program = createProgram(gl, VERT_SRC, FRAG_CYBER);
    if (!program) return;

    gl.useProgram(program);

    const positions = new Float32Array([-1, -1,  1, -1,  -1,  1,  1,  1]);
    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, positions, gl.STATIC_DRAW);

    const posLoc = gl.getAttribLocation(program, 'a_position');
    gl.enableVertexAttribArray(posLoc);
    gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);

    const uTime       = gl.getUniformLocation(program, 'u_time');
    const uResolution = gl.getUniformLocation(program, 'u_resolution');

    const syncSize = () => {
      const dpr = window.devicePixelRatio || 1;
      const w = Math.round(canvas.clientWidth  * dpr);
      const h = Math.round(canvas.clientHeight * dpr);
      if (w > 0 && h > 0) {
        canvas.width  = w;
        canvas.height = h;
        gl.viewport(0, 0, w, h);
        gl.uniform2f(uResolution, w, h);
      }
    };
    syncSize();
    if (canvas.width === 0) {
      canvas.width = window.innerWidth; canvas.height = window.innerHeight;
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.uniform2f(uResolution, canvas.width, canvas.height);
    }

    const observer = new ResizeObserver(syncSize);
    observer.observe(canvas);

    let rafId;
    const draw = () => {
      gl.uniform1f(uTime, performance.now() / 1000);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      rafId = requestAnimationFrame(draw);
    };
    rafId = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(rafId);
      observer.disconnect();
      gl.deleteBuffer(buffer);
      gl.deleteProgram(program);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        top: 0, left: 0, right: 0, bottom: 0,
        width: '100%',
        height: '100%',
        display: 'block',
        pointerEvents: 'none',
      }}
    />
  );
}

// ─── Public ───────────────────────────────────────────────────────────────────

export default function ShaderBackground() {
  const { isCyberpunk } = useTheme();

  return (
    <div
      aria-hidden="true"
      style={{
        position: 'absolute',
        top: 0, left: 0, right: 0, bottom: 0,
        zIndex: 0,
        pointerEvents: 'none',
        overflow: 'hidden',
      }}
    >
      <AnimatePresence mode="sync">
        <motion.div
          key={isCyberpunk ? 'cyber' : 'clean'}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.7, ease: 'easeInOut' }}
          style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
        >
          {isCyberpunk ? <ShaderCanvas /> : <FluidCanvas />}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
